import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, moveItem } from '@/features/legends/editor/collections'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import type { Question } from '@/features/legends/questions/questions'
import type { FilterScope } from '@/features/legends/filters/filter-scope'
import { match } from '@/utils/pattern-match-util'
import { validateElements } from '@/utils/validx-util'
import { PropertyInspectorViewModel } from './PropertyInspectorViewModel'
import type {
  QuestionEditorViewModelBase,
  QuestionEditorViewModelOptions,
} from './QuestionEditorViewModelBase'
import type { DropPosition, QuestionDragPayload } from './question-drag'
import {
  type AnyQuestionEditor,
  asAnyQuestionEditor,
  createQuestion,
  hydrateQuestion,
} from './question-registry'
import { questionTypeInfo, type QuestionType } from './question-types'

export interface QuestionsBuilderViewModelOptions {
  dependencies: LegendPartDependencies
  /**
   * The path that holds these questions, if any.
   */
  parentQuestion: QuestionEditorViewModelBase | null
  scope?: readonly FilterScope[]
}

/**
 * The legend's questions, or a path's. Nested builders share the root's
 * inspector, ID counters and drag, and the root manages every question in
 * the tree, so moving a question between paths keeps it active.
 */
export class QuestionsBuilderViewModel extends LegendPartViewModelBase {
  private readonly _scope: readonly FilterScope[]
  private readonly _schema: IValidationSchema<QuestionsBuilderViewModel> = {
    questions: [validateElements('There are invalid questions.')],
  }
  private readonly _inspectorVm = new PropertyInspectorViewModel()
  private _idCounters = new Map<string, number>()

  /**
   * Only read while handling drag events, so it isn't observable.
   */
  private _dragPayload: QuestionDragPayload | null = null

  /**
   * The path that holds these questions, if any.
   */
  readonly parentQuestion: QuestionEditorViewModelBase | null

  readonly questions = observable.array<QuestionEditorViewModelBase>([], {
    deep: false,
  })

  readonly validation = validationContext(this, this._schema)

  /**
   * Whether what is being dragged can be dropped at the end of the builder,
   * like into an empty path. A field, since the canvas calls it without the
   * builder.
   */
  readonly canAcceptDrop = () => {
    const payload = this.rootBuilder._dragPayload
    if (!payload || this.isEditingDisabled) {
      return false
    }
    if (payload.kind === 'toolbox') {
      return true
    }
    return !this.parentQuestion?.isChildOf(payload.question)
  }

  constructor({
    dependencies,
    parentQuestion,
    scope,
  }: QuestionsBuilderViewModelOptions) {
    super(dependencies)
    this.parentQuestion = parentQuestion
    this._scope = scope ?? []
    if (!parentQuestion) {
      this.lifecycle.manage(
        manageEach(this.lifecycle, () => this.flattenedQuestions),
      )
    }
  }

  /**
   * Whether the builder has no questions.
   */
  @computed
  get isEmpty() {
    return this.questions.length === 0
  }

  /**
   * Every question in the tree, as entities other parts can reference.
   */
  @computed
  get entityDescriptors(): EntityDescriptor[] {
    return this.allQuestions.map((question) => ({
      type: match<QuestionType, string>(question.type, {
        Variable: 'Variable',
        InventoryItem: 'InventoryItem',
        PersonalOrganizerCollectedValue: 'OrganizerCollectedValue',
        Bool: 'Question',
        Select: 'Question',
        Numeric: 'Question',
        Text: 'Question',
        DateTime: 'Question',
        Breaker: 'Question',
        Documents: 'Question',
        Path: 'Question',
        Effect: 'Question',
        PersonalOrganizerPrefill: 'Question',
      }),
      hidden:
        question.type === 'Path' ||
        question.type === 'Breaker' ||
        question.type === 'Effect' ||
        question.type === 'PersonalOrganizerPrefill',
      entity: question,
    }))
  }

  /**
   * The legend's builder, at the top of the tree.
   */
  @computed
  get rootBuilder(): QuestionsBuilderViewModel {
    return this.parentQuestion
      ? this.parentQuestion.parentQuestionsBuilder.rootBuilder
      : this
  }

  /**
   * The inspector, shared by the whole tree.
   */
  @computed
  get inspectorVm(): PropertyInspectorViewModel {
    return this.rootBuilder._inspectorVm
  }

  /**
   * Every question in the tree.
   */
  @computed
  get allQuestions(): AnyQuestionEditor[] {
    return flattenQuestions(this.rootBuilder.questions)
  }

  /**
   * This builder's questions and the questions nested in them.
   */
  @computed
  get flattenedQuestions(): AnyQuestionEditor[] {
    return flattenQuestions(this.questions)
  }

  /**
   * The question the inspector edits.
   */
  @computed
  get selectedQuestion() {
    return this.inspectorVm.questionVm
  }

  /**
   * Whether the inspector shows: there is something to edit or select.
   */
  @computed
  get isInspectorShown() {
    return !!this.selectedQuestion || this.questions.length > 0
  }

  /**
   * Expands the question's paths, selects it and scrolls to it.
   */
  @action.bound
  reveal(question: QuestionEditorViewModelBase) {
    for (const ancestor of question.ancestors) {
      ancestor.toggleCollapsed(false)
    }
    this.edit(question)
    question.scrollIntoView()
  }

  /**
   * Selects the question in the inspector.
   */
  @action.bound
  edit(question: QuestionEditorViewModelBase) {
    this.inspectorVm.select(question)
  }

  /**
   * Adds a question at the end, or next to `relativeQuestion`: before it when
   * `direction` is 0, after it when 1.
   */
  @action.bound
  add(
    type: QuestionType,
    relativeQuestion: QuestionEditorViewModelBase | null = null,
    direction = 0,
  ) {
    const builder = relativeQuestion
      ? relativeQuestion.parentQuestionsBuilder
      : this
    const question = createQuestion(type, builder._questionOptions)
    // Effects and prefills can't be referenced, so they have no ID.
    if (!question.info.optionalId) {
      question.assignId(this.nextId(questionTypeInfo[type].idPrefix))
    }

    builder.questions.push(question)
    if (relativeQuestion) {
      const collection = builder.questions
      moveItem(
        collection,
        collection.indexOf(question),
        collection.indexOf(relativeQuestion) + direction,
      )
    }

    this.edit(question)
    question.focusIdField()
    return question
  }

  /**
   * Adds an element picked in the toolbox: into the selected path when it is
   * expanded, after the selected question, or at the end.
   */
  @action.bound
  addFromToolbox(type: QuestionType) {
    if (this.isEditingDisabled) {
      return
    }
    const selected = this.selectedQuestion
    let question: AnyQuestionEditor
    if (!selected) {
      question = this.add(type)
    } else if (selected.type === 'Path' && !selected.isCollapsed) {
      question = selected.questions.add(type)
    } else {
      question = this.add(type, selected, 1)
    }
    question.scrollIntoView()
  }

  /**
   * Moves `source` next to `target`, or into this builder when there is no
   * target (dropping into an empty path).
   */
  @action.bound
  move(
    source: QuestionEditorViewModelBase,
    target: QuestionEditorViewModelBase | null,
    direction = 0,
  ) {
    if (source === target) {
      return
    }

    const sourceBuilder = source.parentQuestionsBuilder
    if (!target) {
      sourceBuilder.questions.remove(source)
      this.questions.push(source)
      source.setParentQuestionsBuilder(this)
      return
    }

    const targetBuilder = target.parentQuestionsBuilder
    if (sourceBuilder !== targetBuilder) {
      sourceBuilder.questions.remove(source)
      targetBuilder.questions.push(source)
      source.setParentQuestionsBuilder(targetBuilder)
    }

    const questions = targetBuilder.questions
    const fromIndex = questions.indexOf(source)
    let toIndex = questions.indexOf(target) + direction
    // Removing the source shifts everything after it up by one.
    if (sourceBuilder === targetBuilder && fromIndex < toIndex) {
      toIndex--
    }
    moveItem(questions, fromIndex, toIndex)
  }

  /**
   * Removes the question, deselecting it or the question selected inside it.
   */
  @action.bound
  remove(question: QuestionEditorViewModelBase) {
    if (this.inspectorVm.questionVm?.isChildOf(question)) {
      this.inspectorVm.deselect()
    }
    this.questions.remove(question)
  }

  /**
   * Removes the selected question, unless the legend can't be edited.
   */
  @action.bound
  removeSelected() {
    if (!this.isEditingDisabled) {
      this.selectedQuestion?.remove()
    }
  }

  /**
   * The next unused ID with the prefix, counting up per prefix.
   */
  @action
  nextId(prefix = 'QUESTION_') {
    const root = this.rootBuilder
    const ids = new Set(this.allQuestions.map((q) => q.id))
    const key = prefix.toUpperCase()
    let count = root._idCounters.get(key) ?? 1
    for (;;) {
      const id = `${prefix}${count++}`
      if (!ids.has(id)) {
        root._idCounters.set(key, count)
        return id
      }
    }
  }

  /**
   * Inserts a copy of the question after it, with fresh IDs.
   */
  @action
  duplicate(question: QuestionEditorViewModelBase) {
    const index = this.questions.indexOf(question)
    const copy = hydrateQuestion(question.toJSON(), this._questionOptions)
    this.questions.push(copy)
    this._generateNewIds(copy)
    if (index > -1) {
      moveItem(this.questions, this.questions.indexOf(copy), index + 1)
    }
    this.edit(copy)
  }

  /**
   * Creates the builder for a path's questions.
   */
  createChildBuilder(parentQuestion: QuestionEditorViewModelBase) {
    return new QuestionsBuilderViewModel({
      dependencies: this.dependencies,
      parentQuestion,
      scope: this._scope,
    })
  }

  /**
   * Starts dragging something onto the canvas.
   */
  @action.bound
  startDrag(payload: QuestionDragPayload) {
    this.rootBuilder._dragPayload = payload
  }

  /**
   * Starts dragging a new element from the toolbox.
   */
  @action.bound
  startToolboxDrag(type: QuestionType) {
    this.startDrag({ kind: 'toolbox', type })
  }

  /**
   * Ends the drag, dropped or not.
   */
  @action.bound
  endDrag() {
    this.rootBuilder._dragPayload = null
  }

  /**
   * Whether what is being dragged can be dropped next to `target`. A
   * question can't be dropped next to itself or into its own path.
   */
  canDropNextTo(target: QuestionEditorViewModelBase) {
    const payload = this.rootBuilder._dragPayload
    if (!payload || target.isEditingDisabled) {
      return false
    }
    return payload.kind === 'toolbox' || !target.isChildOf(payload.question)
  }

  /**
   * Drops what is being dragged next to `target`, before or after it, ending
   * the drag.
   */
  @action.bound
  dropNextTo(target: QuestionEditorViewModelBase, position: DropPosition) {
    const payload = this.rootBuilder._dragPayload
    if (!payload || !this.canDropNextTo(target)) {
      return
    }
    const direction = position === 'above' ? 0 : 1
    const builder = target.parentQuestionsBuilder
    if (payload.kind === 'toolbox') {
      builder.add(payload.type, target, direction)
    } else {
      builder.move(payload.question, target, direction)
      builder.edit(payload.question)
    }
    this.endDrag()
  }

  /**
   * Drops what is being dragged at the end of the builder, ending the drag.
   */
  @action.bound
  acceptDrop() {
    const payload = this.rootBuilder._dragPayload
    if (!payload || !this.canAcceptDrop()) {
      return
    }
    if (payload.kind === 'toolbox') {
      this.add(payload.type)
    } else {
      this.move(payload.question, null)
      this.edit(payload.question)
    }
    this.endDrag()
  }

  /**
   * Sets the questions from the legend document. Throws for an element the
   * builder has no editor for.
   */
  @action
  hydrate(questions: Question[]) {
    this._idCounters = new Map()
    this.questions.replace(
      questions.map((q) => hydrateQuestion(q, this._questionOptions)),
    )
  }

  /**
   * Shows the analyzer's errors on the questions.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'questions',
      'There are invalid questions.',
    )
    receiveErrors(this.questions, descriptor?.inner)
  }

  /**
   * The questions as the legend document has them.
   */
  toJSON(): Question[] {
    return this.questions.map((x) => x.toJSON())
  }

  private get _questionOptions(): QuestionEditorViewModelOptions {
    return {
      dependencies: this.dependencies,
      parentQuestionsBuilder: this,
      scope: this._scope,
    }
  }

  @action
  private _generateNewIds(question: QuestionEditorViewModelBase) {
    for (const q of [question, ...question.getAllSubQuestions()]) {
      if (q.info.optionalId) {
        continue
      }
      const lastKnown = q.lastKnownId
      q.assignId(this.nextId(lastKnown ? `${lastKnown}_` : 'QUESTION_'))
      q.lastKnownId = lastKnown ?? q.id
    }
  }
}

function flattenQuestions(
  questions: QuestionEditorViewModelBase[],
): AnyQuestionEditor[] {
  return questions
    .flatMap((q) => [q, ...q.getAllSubQuestions()])
    .map(asAnyQuestionEditor)
}
